"use client";

import { Header, Footer } from "@/components/common";

export default function PrivacyPolicyClient() {
  return (
    <div className="min-h-screen bg-[#212121]">
      <Header />

      {/* Hero */}
      <section className="pt-40 pb-16 bg-[#212121]">
        <div className="container mx-auto px-6 max-w-6xl">
          <h1 className="text-4xl lg:text-5xl font-medium tracking-tight text-[#F8F7F4] mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-[#A4A4A4]">
            Your privacy is important to us. Learn how we collect, use, and
            protect your information.
          </p>
          <p className="text-sm text-[#909090] mt-4">
            Last updated: 31 March 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="container mx-auto px-6 max-w-6xl pb-24">
        <div className="space-y-12">
          {/* Introduction */}
          <section>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              This Privacy Policy (&quot;Policy&quot;) explains the information
              collection, use, and sharing practices of Shop Circle Holdings Ltd
              (trading as Keystone), One Kingdom Street, Paddington Central,
              London W2 6BD, United Kingdom (&quot;we,&quot; &quot;us,&quot;
              &quot;Keystone&quot; and &quot;our&quot;).
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              Shop Circle Holdings Ltd reserves the right to update this Privacy
              Policy from time to time. Your continued use of our Services after
              any such changes constitutes your acceptance of the revised
              Policy.
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              Shop Circle Holdings Ltd has designed its applications to be used
              by merchants (&quot;Store Owners&quot;) on the Shopify platform.
              Unless otherwise stated, references to collecting, using, or
              disclosing personal information in this Policy describe actions
              taken by the Store Owner (as the data controller), carried out
              through the functionality of our applications.
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              Before you use or submit any information through or in connection
              with the Services, please carefully review this Privacy Policy. By
              using any part of the Services, you understand that your
              information will be collected, used, and disclosed as outlined in
              this Privacy Policy.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Information We Collect
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              We collect information in multiple ways, including when you
              provide information directly to us, and when third parties or
              automated systems provide information to us.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Information You Provide Directly */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Information You Provide Directly
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              Some areas of the Services may require you to submit information
              in order for you to benefit from the specified features (such as
              newsletter subscriptions, tips/pointers, or order processing) or
              to participate in a particular activity. You will be informed what
              information is required and what information is optional.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Information from Third-Party Sources */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Information from Third-Party Sources
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              We may also obtain information from other sources, such as the
              Shopify platform, and combine that with information we collect
              about you. This includes order data, customer data, and store data
              that merchants share with us through installing and using our
              applications.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Information Automatically Collected */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Information Automatically Collected
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              We automatically collect certain information when you visit the
              Services. This information includes your IP address, browser type,
              device type, operating system, referring URLs, and information
              about the usage of our Services, including information collected
              through cookies, pixel tags, and other tracking technologies.
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              We use Google Analytics and Amplitude for web analytics. Google
              Analytics and Amplitude collect and process data about your use of
              the Services.
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              The specific categories of personal data we process through the
              Keystone applications include: contact information (name, email
              address, phone number, WhatsApp); order and checkout data (order
              ID, products ordered, quantities, discounts, taxes, shipping,
              custom properties); device and usage information (IP address,
              browser type, device info, session behaviour); location data
              (country, city, country code); marketing preferences (newsletter
              opt-ins, campaign data); app usage analytics (feature usage,
              dashboard interactions); cookie data (session ID, CSRF token,
              Shopify OAuth); and customer birthdays (date of birth, where
              provided for loyalty rewards). The specific categories collected
              depend on which Keystone application(s) the Store Owner uses.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Aggregate/De-Identified Information */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Aggregate/De-Identified Information
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              We may aggregate and/or de-identify any information collected
              through the Services so that such information can no longer be
              linked to you or your device. We may use such information for any
              purpose, including without limitation for research and marketing
              purposes, and may also share such data with any third parties.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              How We Use Your Information
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="space-y-2 text-base text-[#A4A4A4] list-disc pl-5">
              <li>To provide, maintain, and improve the Services;</li>
              <li>
                To communicate with you, including to respond to your comments,
                questions, and requests;
              </li>
              <li>
                To monitor and analyse trends, usage, and activities in
                connection with our Services;
              </li>
              <li>
                To detect, investigate, and prevent fraudulent transactions and
                other illegal activities;
              </li>
              <li>
                To personalise and improve the Services and provide content or
                features that match your profile and interests;
              </li>
              <li>
                For any other purpose for which the information was collected.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* How We Share Your Information */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              How We Share Your Information
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              We may share your information in the following situations:
            </p>
            <ul className="space-y-2 text-base text-[#A4A4A4] list-disc pl-5">
              <li>
                With third-party vendors, consultants, and other service
                providers who need access to such information to carry out work
                on our behalf;
              </li>
              <li>
                In response to a request for information if we believe
                disclosure is in accordance with, or required by, any applicable
                law, regulation, or legal process;
              </li>
              <li>
                If we believe your actions are inconsistent with our user
                agreements or policies, or to protect the rights, property, and
                safety of us or others;
              </li>
              <li>
                In connection with, or during negotiations of, any merger, sale
                of company assets, financing, or acquisition of all or a portion
                of our business by another company;
              </li>
              <li>With your consent or at your direction.</li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Third-Party Services and Websites */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Third-Party Services and Websites
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              The Services may contain content from and hyperlinks to websites,
              locations, platforms, and services operated and owned by third
              parties. These third parties may use your information in ways that
              differ from this Privacy Policy. We encourage you to review the
              privacy policies of these third parties.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Online Analytics */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Online Analytics
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              We use third-party web analytics services (such as Google
              Analytics and Amplitude) on our Services to collect and analyse
              usage information through cookies and similar technologies. The
              information is used to analyse the use of the Services, including
              the frequency with which users visit various parts of the Services
              and what features they use.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* EU and UK Data Subject Rights */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              EU and UK Data Subject Rights
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              If you are located in the European Economic Area
              (&quot;EEA&quot;), the United Kingdom and other regions with laws
              governing data collection and use that may differ from laws in the
              United States, please note that we may transfer information,
              including personal information, to a country and jurisdiction that
              does not have the same data protection laws as your jurisdiction.
              We may transfer your data to the United States, where our
              application infrastructure operates.
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              Where we transfer personal data outside the EEA or the United
              Kingdom, we ensure appropriate safeguards are in place, including
              EU Standard Contractual Clauses (Commission Implementing Decision
              (EU) 2021/914) and/or the UK International Data Transfer Addendum
              issued by the ICO under Section 119A of the Data Protection Act
              2018. Our primary application data servers are located in
              Frankfurt, Germany (European Union).
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              Under certain circumstances, you have rights under data protection
              laws in relation to your personal data, including the right to:
            </p>
            <ul className="space-y-2 text-base text-[#A4A4A4] list-disc pl-5 mb-4">
              <li>Request access to your personal data</li>
              <li>Request correction of your personal data</li>
              <li>Request erasure of your personal data</li>
              <li>Object to processing of your personal data</li>
              <li>Request restriction of processing</li>
              <li>Request transfer of your personal data</li>
              <li>Right to withdraw consent</li>
              <li>
                Right to lodge a complaint with a supervisory authority (in the
                UK, the Information Commissioner&apos;s Office (ICO); in the EU,
                the relevant national data protection authority)
              </li>
            </ul>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              If you wish to exercise any of these rights, please contact us at
              info@key-stone.app.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Data Controller and Processor */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Data Controller and Processor
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              For the purposes of applicable data protection law, the Store
              Owner (merchant) is the data controller in respect of the personal
              data of their customers that is processed through our
              applications. Shop Circle Holdings Ltd acts as a data processor on
              behalf of the Store Owner. In relation to certain analytics, usage
              data, and account information, Shop Circle Holdings Ltd acts as an
              independent data controller.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Children&apos;s Privacy
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              Our Services are not directed to individuals under the age of 16.
              We do not knowingly collect personal data from children under 16.
              If you become aware that a child has provided us with personal
              data, please contact us at info@key-stone.app and we will take
              steps to delete such information.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Right to Complain */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Right to Complain
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              If you are unhappy with how we have handled your personal data,
              you have the right to lodge a complaint with a supervisory
              authority. In the United Kingdom, this is the Information
              Commissioner&apos;s Office (ICO) at https://ico.org.uk. In the EU,
              you may contact your local data protection authority.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Security */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Security
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              We take steps to ensure that your information is treated securely
              and in accordance with this Privacy Policy. Unfortunately, no
              system is 100% secure, and we cannot ensure or warrant the
              security of any information you provide to us. To the fullest
              extent permitted by applicable law, we do not accept liability for
              unauthorised access, use, disclosure, or loss of personal
              information.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Changes
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              We may update this Privacy Policy from time to time to reflect
              changes in our practices, legal requirements, or for other
              operational, legal, or regulatory reasons. We encourage you to
              review this Policy periodically.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Governing Law
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              This Privacy Policy and any disputes arising out of or in
              connection with it shall be governed by and construed in
              accordance with the laws of England and Wales. The courts of
              England and Wales shall have non-exclusive jurisdiction.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Contact
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              For questions about this Privacy Policy, contact us at
              info@key-stone.app.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
